import * as React from 'react'
import { useTheme } from '@mui/material/styles'
import Box from '@mui/material/Box'
import OutlinedInput from '@mui/material/OutlinedInput'
import MenuItem from '@mui/material/MenuItem'
import Chip from '@mui/material/Chip'
import PropTypes from 'prop-types'
import CustomSelectMultiple, { MenuProps, getStyles } from './SelectMultipleInputStyle'
import { useTranslation } from 'react-i18next'
import { Checkbox } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'

const SelectMultipleInput = ({ label, options, onChange, value = [], minWidth = 270 }) => {
  const theme = useTheme()
  const { t } = useTranslation()
  const selectRef = React.useRef(null)

  const handleChange = (event) => {
    let selected = event.target.value
    if (selected.includes('') && selected.length > 1) {
      selected = selected.filter((val) => val !== '')
    }
    onChange(selected)
  }

  const handleRemove = (item) => {
    const updatedValue = value.filter((val) => val !== item)
    onChange(updatedValue)
  }

  const handleReset = (e) => {
    e.stopPropagation()
    onChange([])
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        borderRadius: '25px',
        border: '1px solid #DDDDDD',
        overflow: 'hidden',
        minWidth: minWidth,
        backgroundColor: '#fafafa',
      }}
    >
      <CustomSelectMultiple
        multiple
        displayEmpty
        value={value}
        onChange={handleChange}
        ref={selectRef}
        input={
          <OutlinedInput
            notched={false}
            sx={{
              border: 'none',
              p: 0,
              height: '100%',
              borderRadius: 0,
            }}
          />
        }
        renderValue={(selected) => (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, px: 1 }}>
            {selected.length === 0 ? (
              <span style={{ color: '#ccc' }}>{label ?? t('form.status')}</span>
            ) : (
              selected.map((val) => {
                const opt = options.find((o) => o.val === val)
                return (
                  <Chip
                    key={val}
                    label={opt?.label || val}
                    size="small"
                    onMouseDown={(e) => e.stopPropagation()}
                    onDelete={(e) => {
                      e.stopPropagation()
                      handleRemove(val)
                    }}
                    deleteIcon={<CloseIcon />}
                    sx={{
                      backgroundColor: '#f1f1f1',
                      fontWeight: 500,
                      '& .MuiChip-deleteIcon': {
                        color: '#888',
                        '&:hover': {
                          color: '#f00',
                        },
                      },
                    }}
                  />
                )
              })
            )}
          </Box>
        )}
        MenuProps={MenuProps}
        sx={{ flex: 1, '& fieldset': { border: 'none' } }}
      >
        {options.map((option) => (
          <MenuItem
            key={option.val}
            value={option.val}
            disabled={option.disable}
            style={getStyles(option.val, value, theme)}
          >
            <Checkbox checked={value.includes(option.val)} />
            {option.label}
          </MenuItem>
        ))}
      </CustomSelectMultiple>
      {value.length > 0 && (
        <Box
          onMouseDown={(e) => e.stopPropagation()}
          onClick={handleReset}
          sx={{
            px: 1,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            color: '#999',
            '&:hover': { color: '#f00' },
          }}
        >
          <CloseIcon fontSize="small" />
        </Box>
      )}
    </Box>
  )
}

SelectMultipleInput.propTypes = {
  label: PropTypes.string,
  options: PropTypes.array,
  onChange: PropTypes.func,
  value: PropTypes.any,
  minWidth: PropTypes.number,
}

export default SelectMultipleInput
